import useClientsAdd from '../operations/client/useClientsAdd';
import { Button, Checkbox, Dialog, Field, HStack, Input, InputGroup, Link, Text, Textarea } from '@chakra-ui/react';
import { Client } from '@opanuj/shared/types';
import { createUserWithEmailAndPassword, UserCredential, validatePassword } from 'firebase/auth';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { normalizeClientDraft } from '../operations/client/clientRecord';
import { ref, update } from 'firebase/database';
import { useDatabase } from '../hooks/useDatabase';
import { useEffect, useState } from 'react';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import { useUpdateProfile } from 'react-firebase-hooks/auth';
import { withMask } from 'use-mask-input';

const generateRandomPassword = (length = 20) => {
	const characterSets = [
		'ABCDEFGHJKLMNPQRSTUVWXYZ',
		'abcdefghijkmnopqrstuvwxyz',
		'23456789',
		'!#$%&*+-=?@_'
	];
	const characters = characterSets.join('');
	const randomValues = new Uint32Array(length);
	globalThis.crypto.getRandomValues(randomValues);
	const password = characterSets.map((characterSet, index) =>
		characterSet[randomValues[index] % characterSet.length]
	);

	for (let index = password.length; index < length; index += 1) {
		password.push(characters[randomValues[index] % characters.length]);
	}

	return password
		.map((character, index) => ({ value: randomValues[index], character }))
		.sort((first, second) => first.value - second.value)
		.map(({ character }) => character)
		.join('');
};

export type NewAccountModalMode = 'self-signup' | 'admin' | 'admin-edit';

export type NewAccountModalProps = {
	isOpen: boolean;
	onClose: () => void;
	mode?: NewAccountModalMode;
	initialClient?: Client | null;
};

export const NewAccountModal = ({
	isOpen: isModalOpen,
	onClose: onModalClose,
	mode = 'self-signup',
	initialClient,
}: NewAccountModalProps) => {

	const auth = useFirebaseAuth()
	const database = useDatabase();
	const [updateProfile] = useUpdateProfile(auth);
	const addClient = useClientsAdd()
	const isAdminMode = mode === 'admin' || mode === 'admin-edit';
	const isEditMode = mode === 'admin-edit';

	const [email, setEmail] = useState('');
	const [emailInputErrorMessage, setEmailInputErrorMessage] = useState('');
	const [pass, setPass] = useState('');
	const [passInputErrorMessage, setPassInputErrorMessage] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [name, setName] = useState('');
	const [isNameInvalid, setIsNameInvalid] = useState(false);
	const [phone, setPhone] = useState('');
	const [isPhoneInvalid, setIsPhoneInvalid] = useState(false);
	const [address, setAddress] = useState('');
	const [isAddressInvalid, setIsAddressInvalid] = useState(false);
	const [postalCode, setPostalCode] = useState('');
	const [isPostalCodeInvalid, setIsPostalCodeInvalid] = useState(false);
	const [city, setCity] = useState('');
	const [isCityInvalid, setIsCityInvalid] = useState(false);
	const [notes, setNotes] = useState('');
	const [agreementChecked, setAgreementChecked] = useState(false);

	useEffect(() => {
		if (!initialClient) return;
		setName(initialClient.name ?? '');
		setEmail(initialClient.email ?? '');
		setPhone(initialClient.phone ?? '');
		setAddress(initialClient.billing?.address ?? '');
		setPostalCode(initialClient.billing?.postalCode ?? '');
		setCity(initialClient.billing?.city ?? '');
		setNotes(initialClient.notes ?? '');
		setAgreementChecked(true);
		setIsNameInvalid(false);
		setIsPhoneInvalid(false);
		setIsAddressInvalid(false);
		setIsPostalCodeInvalid(false);
		setIsCityInvalid(false);
		setEmailInputErrorMessage('');
		setPassInputErrorMessage('');
	}, [initialClient]);

	const validateName = (name: string) => {
		return name === '';
	};

	const validateEmail = (email: string) => {
		const isValid = Boolean(
			String(email)
				.toLowerCase()
				.match(
					/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
				)
		);
		if (isValid) setEmailInputErrorMessage('');
		else setEmailInputErrorMessage('Wpisany email jest niepoprawny');
	};

	const validatePass = async (pass: string) => {
		const result = await validatePassword(auth, pass);
		if (result.isValid) setPassInputErrorMessage('');
		else if (!result.meetsMinPasswordLength)
			setPassInputErrorMessage(
				`Hasło musi mieć co najmniej ${result.passwordPolicy.customStrengthOptions.minPasswordLength} znaków`
			);
		else if (!result.meetsMaxPasswordLength)
			setPassInputErrorMessage(
				`Hasło musi mieć co najwyżej ${result.passwordPolicy.customStrengthOptions.maxPasswordLength} znaków`
			);
	};

	const validatePhoneNumber = (phone: string) => {
		return !Boolean(
			String(phone)
				.toLowerCase()
				.match(
					/^(?:(?:(?:(?:\+|00)\d{2})?[ -]?(?:(?:\(0?\d{2}\))|(?:0?\d{2})))?[ -]?(?:\d{3}[- ]?\d{2}[- ]?\d{2}|\d{2}[- ]?\d{2}[- ]?\d{3}|\d{7})|(?:(?:(?:\+|00)\d{2})?[ -]?\d{3}[ -]?\d{3}[ -]?\d{3}))$/
				)
		);
	};

	const validateAddress = (address: string) => {
		return address === '';
	};

	const validatePostalCode = (postalCode: string) => {
		return !Boolean(
			String(postalCode)
				.toLowerCase()
				.match(/\d{2}-\d{3}/)
		);
	};

	const validateCity = (city: string) => {
		return city === '';
	};

	const isFormInvalid = Boolean(
		validateName(name) ||
		!Boolean(email) ||
		Boolean(emailInputErrorMessage) ||
		(!isAdminMode && (!Boolean(pass) || Boolean(passInputErrorMessage))) ||
		validateAddress(address) ||
		validatePostalCode(postalCode) ||
		validateCity(city) ||
		(!isAdminMode && !agreementChecked)
	)

	const handleSubmit = async () => {
		try {
			let clientUid = initialClient?.uid;
			let credentials: UserCredential | undefined;

			let password = pass;
			if (isAdminMode) {
				password = generateRandomPassword();
			}

			credentials = await createUserWithEmailAndPassword(
				auth,
				email,
				password
			);
			clientUid = credentials.user.uid;
			const success = await updateProfile({ displayName: name });

			if (!success) console.error('Display name not updated');
				
			const clientData = normalizeClientDraft({
				uid: clientUid,
				fullAccount: !isAdminMode,
				name,
				email,
				phone,
				address,
				postalCode,
				city,
				billingName: name,
				billingAddress: address,
				billingPostalCode: postalCode,
				billingCity: city,
				notes,
				notifications: { emails: [email] },
			});

			if (isEditMode && initialClient) {
				await update(ref(database, `clients/${initialClient.id}`), {
					...initialClient,
					...clientData,
				});
			} else {
				addClient.mutate(clientData);
			}

			onModalClose();
		} catch (err: any) {
			if (err.code === 'auth/invalid-email')
				setEmailInputErrorMessage('Wpisany email jest niepoprawny');
			if (err.code === 'auth/email-already-in-use')
				setEmailInputErrorMessage(
					'Istnieje już konto założone na podany email'
				);
			if (err.code === 'auth/user-not-found')
				setEmailInputErrorMessage(
					'Nie znaleziono konta z podanym adresem email'
				);
			if (err.code === 'auth/missing-password')
				setPassInputErrorMessage('Hasło nie może być puste');
			if (err.code === 'auth/wrong-password')
				setPassInputErrorMessage('Hasło nie jest poprawne');
		}
	};

	return (
        <Dialog.Root
            open={isModalOpen}
            onOpenChange={(details) => {
                if (!details.open) {
                    onModalClose();
                }
            }}
        >
            <Dialog.Backdrop />
            <Dialog.Positioner>
                <Dialog.Content>
                    <Dialog.Header>{isAdminMode ? 'Dodaj klienta' : 'Załóż nowe konto'}</Dialog.Header>
                    <Dialog.CloseTrigger />
                    <Dialog.Body>
					<form>
						{/* <Heading size='sm' fontStyle='italic'>Dane do kontaku i wystawianych faktur</Heading> */}
						<Field.Root
							mt={5}
							mb={5}
							required
							invalid={isNameInvalid}
						>
							<Field.Label>Imię i nazwisko</Field.Label>
							<Input
								value={name}
								onChange={(event) => {
									setName(event.target.value);
									setIsNameInvalid(
										validateName(event.target.value)
									);
								}}
								onBlur={() =>
									setIsNameInvalid(validateName(name))
								}
							/>
							<Field.ErrorText>
								Pole nie może być puste
							</Field.ErrorText>
						</Field.Root>

						<Field.Root
							mt={5}
							mb={5}
							invalid={Boolean(emailInputErrorMessage)}
							required
						>
							<Field.Label>Email</Field.Label>
							<InputGroup>
								{/* <InputLeftElement
						pointerEvents="none"
						children={<FaEnvelope color="gray.300" />}
					/> */}
								<Input
									value={email}
									onChange={(e: any) =>
										setEmail(e.target.value)
									}
									onBlur={() => validateEmail(email)}
									type="email"
								/>
							</InputGroup>
							<Field.ErrorText>
								{emailInputErrorMessage}
							</Field.ErrorText>
						</Field.Root>
						{!isAdminMode && (
							<Field.Root
								mt={5}
								mb={5}
								invalid={Boolean(passInputErrorMessage)}
								required
							>
								<Field.Label>Hasło</Field.Label>
								<InputGroup
									endElement={<Button
										width="4.5rem"
										h="1.75rem"
										size="sm"
										onClick={() => setShowPassword(!showPassword)}
									>
										{showPassword ? 'Ukryj' : 'Pokaż'}
									</Button>}
								>
									<Input
										value={pass}
										onChange={(e: any) => setPass(e.target.value)}
										onBlur={() => validatePass(pass)}
										type={showPassword ? 'text' : 'password'}
									/>
								</InputGroup>
								<Field.ErrorText>
									{passInputErrorMessage}
								</Field.ErrorText>
							</Field.Root>
						)}
						<Field.Root
							mt={5}
							mb={5}
							required
							invalid={isPhoneInvalid}
						>
							<Field.Label>Telefon</Field.Label>
							<InputGroup startAddon='+48'>
								<Input
									type="tel"
									value={phone}
									ref={withMask("999 999 999")}
									onChange={(event) => {
										setPhone(event.target.value);
										setIsPhoneInvalid(
											validatePhoneNumber(
												event.target.value
											)
										);
									}}
									onBlur={() =>
										setIsPhoneInvalid(
											validatePhoneNumber(phone)
										)
									}
								/>
							</InputGroup>
							<Field.ErrorText>
								Podaj poprawny numer telefonu
							</Field.ErrorText>
						</Field.Root>
						<Field.Root
							mt={5}
							mb={5}
							required
							invalid={isAddressInvalid}
						>
							<Field.Label>Adres</Field.Label>
							<Input
								type="text"
								value={address}
								onChange={(event) => {
									setAddress(event.target.value);
									setIsAddressInvalid(
										validateAddress(event.target.value)
									);
								}}
								onBlur={() =>
									setIsAddressInvalid(
										validateAddress(address)
									)
								}
							/>
							<Field.ErrorText>
								Pole nie może być puste
							</Field.ErrorText>
							<Field.HelperText>* do faktury VAT</Field.HelperText>
						</Field.Root>
						<HStack>
							<Field.Root
								mt={5}
								mb={5}
								required
								invalid={isPostalCodeInvalid}
							>
								<Field.Label>Kod pocztowy</Field.Label>
								<Input
									type="text"
									value={postalCode}
									ref={withMask("99-999")}
									onChange={(event) => {
										setPostalCode(event.target.value);
										setIsPostalCodeInvalid(
											validatePostalCode(
												event.target.value
											)
										);
									}}
									onBlur={() =>
										setIsPostalCodeInvalid(
											validatePostalCode(postalCode)
										)
									}
								/>
								<Field.ErrorText>
									Podaj poprawny kod pocztowy
								</Field.ErrorText>
							</Field.Root>
							<Field.Root
								mt={5}
								mb={5}
								required
								invalid={isCityInvalid}
							>
								<Field.Label>Miasto</Field.Label>
								<Input
									type="text"
									value={city}
									onChange={(event) => {
										setCity(event.target.value);
										setIsCityInvalid(
											validateCity(event.target.value)
										);
									}}
									onBlur={() =>
										setIsCityInvalid(validateCity(city))
									}
								/>
								<Field.ErrorText>
									Pole nie może być puste
								</Field.ErrorText>
							</Field.Root>
						</HStack>
						<Field.Root mt={5} mb={5}>
							<Field.Label>Informacje dodatkowe</Field.Label>
							<Textarea
								value={notes}
								onChange={(event) => {
									setNotes(event.target.value);
								}}
							/>
						</Field.Root>
						{!isAdminMode && (
							<Checkbox.Root
								required
								colorPalette="orange"
								onCheckedChange={(details) => setAgreementChecked(details.checked === true)}
								checked={agreementChecked}
							>
								<Checkbox.HiddenInput />
								<Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
								<Checkbox.Label>
									<Text ml={3} fontSize="sm">
										Oświadczam, że zapoznałem się i akceptuję{' '}
										<Link
											color="mediumblue"
											href="https://opanuj-to.web.app/static/Polityka%20prywatno%C5%9Bci%202024.pdf"
										>
											Politykę przetwarzania danych osobowych{' '}
											<ExternalLinkIcon mx="2px" />
										</Link>
									</Text>
								</Checkbox.Label>
							</Checkbox.Root>
						)}
					</form>
				</Dialog.Body>
				<Dialog.Footer>
					<Button variant="ghost" mr={3} onClick={onModalClose}>
						Anuluj
					</Button>
					<Button
						disabled={isFormInvalid}
						colorPalette="orange"
						onClick={handleSubmit}
					>
						OK
					</Button>
				</Dialog.Footer>
				</Dialog.Content>
            </Dialog.Positioner>
        </Dialog.Root>
    );
};
